import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, PageHeader } from "../../design-system";
import { Icon } from "../../design-system/Icon";
import PortalLayout from "./PortalLayout";
import useInspectionPortal from "./useInspectionPortal";

export default function PortalDashboard() {
  document.title = "Inspección · Dashboard | Kronaly";
  return (
    <PortalLayout>
      <Body />
    </PortalLayout>
  );
}

function Body() {
  const { workspace } = useInspectionPortal();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="Resumen del workspace"
        description="Punto de partida de tu inspección. Desde aquí accedes a las secciones con todos los registros disponibles."
      />

      <Alert tone="info">
        <strong>Acceso de solo lectura.</strong> No puedes modificar datos, solo consultarlos y descargar
        exports firmados. Cada acceso queda registrado con tu IP y momento.
      </Alert>

      <div style={statsGrid}>
        <Stat
          label="Empleados activos"
          value={workspace?.employees_count_active ?? "—"}
        />
        <Stat
          label="Total históricos"
          value={workspace?.employees_count_total ?? "—"}
          hint="incluye bajas conservadas"
        />
        <Stat
          label="Zona horaria"
          value={workspace?.timezone || "UTC"}
        />
      </div>

      <PageHeader title="Acciones disponibles" />

      <div style={cardsGrid}>
        <ActionCard
          icon="Users"
          title="Ver empleados"
          description="Lista completa con sus fechas de alta y baja. Los registros de empleados dados de baja se conservan 4 años (RD-ley 8/2019)."
          cta="Abrir empleados"
          onClick={() => navigate("/inspeccion/empleados")}
        />
        <ActionCard
          icon="Clock"
          title="Consultar jornadas"
          description="Fichajes por empleado y rango de fechas. Puedes descargar la información en PDF, CSV o JSON con firma criptográfica."
          cta="Abrir jornadas"
          onClick={() => navigate("/inspeccion/jornadas")}
        />
        <ActionCard
          icon="FileSearch"
          title="Auditoría de cambios"
          description="Log inmutable con todas las modificaciones del registro horario. Quién, cuándo, qué cambió y desde qué IP."
          cta="Abrir auditoría"
          onClick={() => navigate("/inspeccion/auditoria")}
        />
        <ActionCard
          icon="ShieldCheck"
          title="Verificar un export"
          description="Comprueba la integridad de un documento ya descargado introduciendo su signature_id."
          cta="Abrir verificador"
          onClick={() => navigate("/inspeccion/verificar")}
        />
      </div>
    </>
  );
}

function Stat({ label, value, hint }) {
  return (
    <Card>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)" }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "var(--kr-fg-strong)", marginTop: 4 }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>{hint}</div>
      )}
    </Card>
  );
}

function ActionCard({ icon, title, description, cta, onClick }) {
  return (
    <Card>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div
          style={{
            background: "var(--kr-primary-50, #eef2ff)",
            color: "var(--kr-primary-700, #4338ca)",
            borderRadius: 8,
            padding: 10,
            flexShrink: 0,
          }}
        >
          <Icon name={icon} size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{title}</div>
          <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", marginTop: 4, marginBottom: 12 }}>
            {description}
          </div>
          <Button variant="secondary" size="sm" icon="ArrowRight" onClick={onClick}>
            {cta}
          </Button>
        </div>
      </div>
    </Card>
  );
}

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 12,
  marginBottom: 24,
};

const cardsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};
